// @flow
/* eslint arrow-parens:0 */
import pubSub from 'app/common/lib/PubSub';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import type { Action, State } from 'app/common/redux/types';
import type { Dispatch, MiddlewareAPI } from 'redux';
import {
  experienceFetchAll,
  experienceFetchAllComplete,
  experienceFetch,
  experienceFetchComplete,
  experienceRemoval,
  placementExperimentsTrigger,
} from 'app/common/redux/actions/experiences';
import {
  isRenderableExperience,
  getPlacedExperienceId,
} from 'app/common/lib/react/experiences/experienceUtils';

// $FlowFixMe: New error after Flow Upgrade http://pinch.pinadmin.com/flow53upgdoc
export default (store: MiddlewareAPI) => (next: Dispatch<any>) => (action: Action): State => {
  const { eligibleExperiences } = store.getState().experiences;

  if (action.type === 'EXPERIENCE_FETCH_ALL') {
    const { isFetchingAll } = store.getState().experiences;
    if (!isFetchingAll) {
      ResourceFactory.create('UserExperiencePlatformResource')
        .callGet()
        .then(
          (response) => {
            const { data: newEligibleExperiences } = response.resource_response;
            store.dispatch(experienceFetchAllComplete(newEligibleExperiences));
          },
          (error) => {
            store.dispatch(experienceFetchAllComplete({}));
          },
        );
    }
  } else if (action.type === 'EXPERIENCE_FETCH_ALL_COMPLETE') {
    const { eligibleExperiences } = action.payload;
    // Syncing from React experiences to Denzel experiences; to remove after we ship React
    pubSub.instance.publish(
      PCONST.PUBSUB_CHANNEL_SITE,
      PCONST.PUBSUB_TOPIC_PLACED_EXPERIENCES_FROM_SERVER,
      eligibleExperiences,
    );
  } else if (action.type === 'EXPERIENCE_FETCH') {
    const { extraContext, placementId } = action.payload;
    if (!!placementId && !isRenderableExperience(eligibleExperiences[placementId])) {
      // prevent repeated requests with the same context
      const oldContext = eligibleExperiences[placementId]
        ? eligibleExperiences[placementId].extraContext
        : null;
      if (JSON.stringify(oldContext) === JSON.stringify(extraContext)) {
        return next(action);
      }
      ResourceFactory.create('UserExperienceResource', {
        placement_ids: [placementId],
        extra_context: extraContext,
      })
        .callGet()
        .then(
          (response) => {
            const { data: newEligibleExperiences } = response.resource_response;
            next(experienceFetchComplete(newEligibleExperiences, placementId, extraContext));
          },
          (error) => {
            next(experienceFetchComplete(eligibleExperiences, placementId, extraContext));
          },
        );
    }
  } else if (action.type === 'PLACEMENT_EXPERIMENTS_TRIGGER') {
    const { placementId, extraContext } = action.payload;
    const experience = eligibleExperiences[placementId];

    if (
      experience &&
      experience.triggerable_placed_exps &&
      experience.triggerable_placed_exps.length
    ) {
      experience.triggerable_placed_exps.forEach((placedExperienceId) => {
        let newExtraContext = extraContext;

        // We need to split the placedExperienceId into placementId and experienceId
        const splitId = placedExperienceId.split(':');
        const placementId = parseInt(splitId[0], 10);
        const experienceId = parseInt(splitId[1], 10);
        const experience = eligibleExperiences[placementId] || {};
        if (experience.metadata && experience.metadata[experienceId]) {
          newExtraContext = Object.assign({}, extraContext, experience.metadata[experienceId]);
        }

        ResourceFactory.create('UserExperienceTriggerResource', {
          placed_experience_id: placedExperienceId.replace(':', '%3A'),
          extra_context: newExtraContext,
        }).callUpdate();
      });
    }
  } else if (action.type === 'EXPERIENCE_VIEWED') {
    const { placementId, experienceId, isBackendExperience } = action.payload;
    const placedExperienceId = getPlacedExperienceId(
      placementId,
      experienceId,
      eligibleExperiences,
      isBackendExperience,
    );
    if (placedExperienceId) {
      ResourceFactory.create('UserExperienceViewedResource', {
        placed_experience_id: placedExperienceId,
      }).callUpdate();
    }
  } else if (action.type === 'EXPERIENCE_DISMISSED') {
    const { placementId, experienceId, preventRemoval, isBackendExperience } = action.payload;
    const placedExperienceId = getPlacedExperienceId(
      placementId,
      experienceId,
      eligibleExperiences,
      isBackendExperience,
    );
    if (placedExperienceId) {
      ResourceFactory.create('UserExperienceResource', {
        placed_experience_id: placedExperienceId,
      })
        .callDelete()
        .then(() => store.dispatch(experienceFetchAll()));
      if (!preventRemoval) {
        next(experienceRemoval(placementId, experienceId));
      }
    }
  } else if (action.type === 'EXPERIENCE_COMPLETED') {
    const {
      placementId,
      experienceId,
      preventRemoval,
      isBackendExperience,
      extraContext,
    } = action.payload;
    const placedExperienceId = getPlacedExperienceId(
      placementId,
      experienceId,
      eligibleExperiences,
      isBackendExperience,
    );
    if (placedExperienceId) {
      ResourceFactory.create('UserExperienceCompletedResource', {
        placed_experience_id: placedExperienceId,
        extra_context: extraContext,
      })
        .callUpdate()
        .then(() => store.dispatch(experienceFetchAll()));
      if (!preventRemoval) {
        next(experienceRemoval(placementId, experienceId));
      }
    }
  } else if (action.type === 'PLACEMENT_MOUNT') {
    const { extraContext, placementId, isServerSideRender } = action.payload;
    const experience = eligibleExperiences[placementId];

    if (!isServerSideRender) {
      store.dispatch(placementExperimentsTrigger(placementId, extraContext));
    }

    if (!isRenderableExperience(experience) && extraContext && Object.keys(extraContext).length) {
      // fetch a contextual experience if we don't currently have an experience
      store.dispatch(experienceFetch(placementId, extraContext));
    }
  }

  return next(action);
};
